import React, { useState } from 'react';
import { X } from 'lucide-react';
import styles from './SyncFromVlScopeModal.module.css';

export type SyncFromVlScope =
  | 'merge_split'
  | 'share_status'
  | 'price'
  | 'barcode'
  | 'seat'
  | 'hold'
  | 'notes'
  | 'marketplaces'
  | 'event';

export const SYNC_FROM_VL_SCOPE_OPTIONS: Array<{
  id: SyncFromVlScope;
  label: string;
  description: string;
}> = [
  {
    id: 'merge_split',
    label: 'Merge / split (VL ids)',
    description:
      'Rematch by production id + date + section/row/seat and update VL listing / item ids',
  },
  {
    id: 'share_status',
    label: 'Share / unshare',
    description: 'Shared flag only — does not change Listed / Verified status',
  },
  {
    id: 'price',
    label: 'Price',
    description: 'List price (and sold amount if Sold) only',
  },
  {
    id: 'barcode',
    label: 'Barcode',
    description: 'Barcode and barcode-attached flag only',
  },
  {
    id: 'seat',
    label: 'Section / row / seat',
    description: 'Section, row, and seat number only',
  },
  {
    id: 'hold',
    label: 'Hold',
    description: 'On-hold flag only',
  },
  {
    id: 'notes',
    label: 'Notes',
    description: 'Internal and external notes only',
  },
  {
    id: 'marketplaces',
    label: 'Marketplaces',
    description: 'Enabled marketplaces from VL notes only',
  },
  {
    id: 'event',
    label: 'Event',
    description: 'Event name, venue, and VL production id only',
  },
];

type Props = {
  open: boolean;
  confirming?: boolean;
  seatCount: number;
  onClose: () => void;
  onConfirm: (scopes: SyncFromVlScope[]) => void | Promise<void>;
};

const SyncFromVlScopeModal: React.FC<Props> = ({
  open,
  confirming = false,
  seatCount,
  onClose,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<SyncFromVlScope[]>(['merge_split']);

  if (!open) return null;

  const toggleScope = (scope: SyncFromVlScope) => {
    setSelected((current) =>
      current.includes(scope)
        ? current.filter((item) => item !== scope)
        : [...current, scope],
    );
  };

  return (
    <div className={styles.overlay}>
      <div
        className={styles.content}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sync-from-vl-scope-title"
      >
        <div className={styles.header}>
          <div>
            <h3 id="sync-from-vl-scope-title">Sync from VL</h3>
            <p className={styles.subtitle}>
              Choose what to sync for {seatCount} seat{seatCount === 1 ? '' : 's'}. Only that
              information is updated in QA.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => !confirming && onClose()}
            aria-label="Close"
            disabled={confirming}
          >
            <X size={18} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.options} role="group" aria-label="Sync scope">
            {SYNC_FROM_VL_SCOPE_OPTIONS.map((option) => (
              <label key={option.id} className={styles.option}>
                <input
                  type="checkbox"
                  checked={selected.includes(option.id)}
                  onChange={() => toggleScope(option.id)}
                  disabled={confirming}
                />
                <span>
                  <strong>{option.label}</strong>
                  <div className={styles.optionDesc}>{option.description}</div>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={confirming}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            disabled={confirming || selected.length === 0}
            onClick={() => void onConfirm(selected)}
          >
            {confirming ? 'Syncing…' : 'Sync'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SyncFromVlScopeModal;
